import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { sentenceCase } from '../../../../utils/utils';
import Text from '../../../elements/text/Text';
import TextInput from '../../../elements/text-input/TextInput';
import Modal from '../../../sections/modal/Modal';
import ModalButtons from '../../../sections/modal/ModalButtons';

const RenameButton = ({ action, loading, selectedFiles }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const formSchema = object({
    name: string()
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('fileName', { ns: 'fields' }),
          })
        )
      )
      .matches(
        /^([a-zA-Z0-9][^:?]*)$/,
        sentenceCase(
          t('invalid', {
            ns: 'validations',
            field: t('fileName', { ns: 'fields' }),
          })
        )
      )
      .max(255, t('maxLength', { ns: 'validations', max: '255' })),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    getValues,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(formSchema),
  });

  const toggleModal = () => setOpen(!open);

  const handleRename = async () => {
    const newName = getValues('name');
    toggleModal();
    await action(newName);
  };
  return (
    <>
      <Button
        startIcon={<DriveFileRenameOutlineIcon />}
        onClick={toggleModal}
        size="small"
        variant="text"
        color="inherit"
        disabled={loading || selectedFiles.length !== 1}
      >
        {t('buttons.rename', { ns: 'fileBrowser' })}
      </Button>
      <Modal
        handleClose={toggleModal}
        openModal={open}
        title={t('folderEdit', { ns: 'instance' })}
        action={
          <ModalButtons
            actionProps={{ disabled: !isDirty || !isValid }}
            actionText={t('save', { ns: 'buttons' })}
            onAction={handleRename}
            onCancel={toggleModal}
          />
        }
      >
        <Text sx={{ mb: 4 }}>
          {t('descriptions.rename', { ns: 'fileBrowser' })}
        </Text>
        <form onSubmit={handleSubmit(handleRename)}>
          <TextInput
            name="name"
            label={t('fileName', { ns: 'fields' })}
            control={control}
            errors={errors}
            autoFocus
            fullWidth
            multiline={false}
          />
        </form>
      </Modal>
    </>
  );
};

RenameButton.propTypes = {
  action: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  selectedFiles: PropTypes.array.isRequired,
};

export default RenameButton;
